import React, { useState } from "react";
import { startOfDay, endOfDay, addDays, differenceInDays } from "date-fns";
import { StatsCardType } from "../../components/StatsCard/StatsCard.types";
import {
  ContactHistoryOverviewFilterView,
  ContactHistoryOverview,
} from "../../app/GeneratedApi";
import {
  putContactHistoryOverviewQuery,
  usePutCustomerAudienceMembershipQuery,
} from "../../app/useGeneratedApi";
import { getInitialDateRange } from "../../utils/dateRangeStorage";
import {
  Check,
  CursorDefaultClick,
  EmailFastOutline,
  MailOpen,
  CalendarCheck,
  CloseBig,
  WarningOutline,
} from "@tiny/icons";
import { useRefreshRegister } from "../../components/RefreshProvider";
import { formatDate } from "@tiny/utils/dateUtils";

export const useCustomerHistory = (
  initialStart: Date = startOfDay(addDays(new Date(), -90)),
  initialEnd: Date = startOfDay(new Date())
) => {
  const customerEmail = getCustomerEmailFromQuery();

  const getInitialRange = () => {
    const fallbackRange = {
      startDate: initialStart,
      endDate: initialEnd,
    };
    const savedRange = getInitialDateRange(fallbackRange);
    return {
      startDate: startOfDay(savedRange.startDate),
      endDate: endOfDay(savedRange.endDate),
    };
  };

  const [selectedRange, setSelectedRange] = useState(getInitialRange);

  const handleDateChange = ({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }) => {
    setSelectedRange({
      startDate: startOfDay(startDate),
      endDate: endOfDay(endDate),
    });
  };

  const filter = React.useMemo((): ContactHistoryOverviewFilterView => {
    const filterData: ContactHistoryOverviewFilterView = {
      from: selectedRange.startDate.getTime(),
      to: selectedRange.endDate.getTime(),
    };

    if (customerEmail) {
      filterData.email = decodeURIComponent(customerEmail);
    }

    return filterData;
  }, [customerEmail, selectedRange.startDate, selectedRange.endDate]);

  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    isError: isOverviewError,
    error: overviewError,
    refetch: refetchOverview,
  } = putContactHistoryOverviewQuery(filter, {
    enabled: true,
  });

  const resolvedEmail = customerEmail
    ? decodeURIComponent(customerEmail)
    : undefined;

  const { data: audienceMembership } = usePutCustomerAudienceMembershipQuery(
    { email: resolvedEmail ?? "" },
    { enabled: !!resolvedEmail }
  );
  const isSuppressed = audienceMembership?.suppressed ?? false;

  const formatTimestampToDate = React.useCallback(
    (timestamp: number): string => {
      if (!timestamp || timestamp === 0) return "N/A";
      return formatDate(new Date(timestamp), "MMM dd yyyy");
    },
    []
  );

  const getDaysAgo = React.useCallback((timestamp: number): string => {
    if (!timestamp || timestamp === 0) return "";
    const days = differenceInDays(new Date(), new Date(timestamp));
    if (days === 0) return "Today";
    if (days === 1) return "1 day ago";
    return `${days} days ago`;
  }, []);

  const customerDetails = React.useMemo((): StatsCardType[] => {
    if (!overviewData) return [];
    return [
      {
        title: "Email",
        value: overviewData?.email?.toString() || "N/A",
        icon: null,
      },
      {
        title: "First Name",
        value: overviewData?.firstName?.toString() || "N/A",
        icon: null,
      },
      {
        title: "Last Name",
        value: overviewData?.lastName?.toString() || "N/A",
        icon: null,
      },
      {
        title: "Status",
        value: overviewData?.status?.toString() || "N/A",
        icon: null,
      },
      {
        title: "Blacklisted / Bounced",
        value:
          overviewData?.blacklisted === true && overviewData?.bounced === true
            ? "Blacklisted & Bounced"
            : overviewData?.blacklisted === true
            ? "Blacklisted"
            : overviewData?.bounced === true
            ? "Bounced"
            : "N/A",
        icon: null,
      },
    ];
  }, [overviewData]);

  const recentActivityStats = React.useMemo((): StatsCardType[] => {
    if (!overviewData) return [];
    return [
      {
        title: "Last Sent",
        value: formatTimestampToDate(overviewData?.lastSent),
        subtitle: getDaysAgo(overviewData?.lastSent),
        icon: (
          <CalendarCheck color="primary" sx={{ fontSize: 20, marginTop: 1 }} />
        ),
      },
      {
        title: "Last Open",
        value: formatTimestampToDate(overviewData?.lastOpen),
        subtitle: getDaysAgo(overviewData?.lastOpen),
        icon: <MailOpen color="info" sx={{ fontSize: 18, marginTop: 1 }} />,
      },
      {
        title: "Last Unsubscribe",
        value: formatTimestampToDate(overviewData?.lastUnsubscribe),
        subtitle: getDaysAgo(overviewData?.lastUnsubscribe),
        icon: <CloseBig color="warning" sx={{ fontSize: 20, marginTop: 1 }} />,
      },
      {
        title: "Last Delivered",
        value: formatTimestampToDate(overviewData?.lastDelivered),
        subtitle: getDaysAgo(overviewData?.lastDelivered),
        icon: (
          <EmailFastOutline
            color="success"
            sx={{ fontSize: 20, marginTop: 1 }}
          />
        ),
      },
      {
        title: "Last Click",
        value: formatTimestampToDate(overviewData?.lastClick),
        subtitle: getDaysAgo(overviewData?.lastClick),
        icon: (
          <CursorDefaultClick
            color="secondary"
            sx={{ fontSize: 18, marginTop: 1 }}
          />
        ),
      },
      {
        title: "Last Spam Report",
        value: formatTimestampToDate(overviewData?.lastSpam),
        subtitle: getDaysAgo(overviewData?.lastSpam),
        icon: (
          <WarningOutline color="error" sx={{ fontSize: 20, marginTop: 1 }} />
        ),
      },
    ];
  }, [overviewData, formatTimestampToDate, getDaysAgo]);

  useRefreshRegister(async () => {
    await refetchOverview();
  });

  return {
    selectedRange,
    handleDateChange,
    customerEmail: resolvedEmail,
    overviewData,
    customerDetails,
    recentActivityStats,
    isOverviewLoading,
    isOverviewError,
    overviewError,
    isSuppressed,
  };
};

const getCustomerEmailFromQuery = () => {
  const pathParts = window.location.pathname.split("/");
  const emailEncoded = pathParts[pathParts.length - 1];
  try {
    return decodeURIComponent(emailEncoded);
  } catch {
    return undefined;
  }
};
